
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FormData } from '@/types/onboarding';

export const useEarlyAccessSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitEarlyAccess = async (formData: FormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      console.log('Submitting early access data:', formData);

      // Prepare data for insertion with proper null handling and validation
      const insertData = {
        first_name: formData.firstName?.trim() || '',
        email: formData.email?.trim() || '',
        motivation: formData.motivation?.trim() || '',
        motivation_other: formData.motivationOther?.trim() || null,
        experience: formData.experience?.trim() || '',
        current_habit_app: formData.currentHabitApp?.trim() || null,
        challenge: formData.challenge?.trim() || '',
        use_case: formData.useCase?.trim() || '',
        use_case_other: formData.useCaseOther?.trim() || null,
        interview_interest: formData.interviewInterest?.trim() || ''
      };

      // Validate required fields before submission
      if (!insertData.first_name || !insertData.email || !insertData.motivation || 
          !insertData.experience || !insertData.challenge || !insertData.use_case || 
          !insertData.interview_interest) {
        throw new Error('All required fields must be filled');
      }

      console.log('Prepared data for insert:', insertData);

      // Save to database
      const { data, error: submitError } = await supabase
        .from('early_access_leads')
        .insert(insertData);

      if (submitError) {
        console.error('Supabase error:', submitError);
        
        // Check if it's a duplicate email error
        if (submitError.message?.includes('duplicate key value violates unique constraint')) {
          console.log('Email already registered, treating as success');
          // Continue to send welcome email and return success
        } else {
          throw submitError;
        }
      } else {
        console.log('Successfully submitted to Supabase:', data);
      }

      // Send welcome email (for new registrations or re-send for existing ones)
      try {
        console.log('Sending welcome email...');
        const emailResponse = await supabase.functions.invoke('send-welcome-email', {
          body: {
            firstName: formData.firstName,
            email: formData.email,
          },
        });

        if (emailResponse.error) {
          console.error('Error sending welcome email:', emailResponse.error);
          // Don't fail the entire process if email fails
        } else {
          console.log('Welcome email sent successfully:', emailResponse.data);
        }
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
        // Don't fail the entire process if email fails
      }

      // Return success regardless of whether it was new registration or duplicate
      return { 
        success: true, 
        data, 
        isExistingUser: submitError?.message?.includes('duplicate key value violates unique constraint') 
      };
    } catch (err) {
      console.error('Error submitting early access data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error saving data';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitEarlyAccess,
    isSubmitting,
    error
  };
};
